
import React from 'react';
import { CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Bookmark, BookmarkCheck, Hourglass, Star } from 'lucide-react';
import { toast } from 'sonner';

interface JobCardHeaderProps {
  title: string;
  companyName?: string;
  jobUrl?: string; // This will be the determinedJobUrl from parent
  isUserLoggedIn: boolean;
  isSaved: boolean;
  isSavingThisJob: boolean;
  score?: number | null;
  onSaveToggle: () => Promise<void>;
  onViewDetails: () => void;
}

export const JobCardHeader: React.FC<JobCardHeaderProps> = ({
  title,
  companyName,
  jobUrl,
  isUserLoggedIn,
  isSaved,
  isSavingThisJob,
  score,
  onSaveToggle,
  onViewDetails,
}) => {

  const handleSaveClick = async () => {
    if (!isUserLoggedIn) {
      toast.error("Please log in to save jobs.");
      return;
    }
    if (isSavingThisJob) return;
    await onSaveToggle();
  };

  const renderLinkOrText = (text: string | undefined, isTitle = false) => {
    if (jobUrl && text) {
      return (
        <a
          href={jobUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`hover:underline ${isTitle ? 'hover:text-primary' : 'text-primary hover:text-primary/80'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {text}
        </a>
      );
    }
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails();
        }}
        className="cursor-pointer"
      >
        {text || (isTitle ? "Untitled Job" : "N/A")}
      </span>
    );
  };
  
  const displayScore = score !== null && score !== undefined && !isNaN(Number(score));
  let scoreText = "";
  let scoreVariant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" = "outline";


  if (displayScore) {
    const numericScore = Number(score);
    if (numericScore >= 0.01 && numericScore <= 1) { // Assuming score is 0-1 for percentage
        scoreText = `${Math.round(numericScore * 100)}% Match`;
    } else if (numericScore > 1) { // Assuming score is out of 100 or similar
        scoreText = `Score: ${Math.round(numericScore)}`;
    } else if (numericScore === 0) {
        scoreText = "Score: 0"; // Explicitly handle 0
    } else { // Handle other cases, possibly NaN or negative if not caught by displayScore
        scoreText = "Score: N/A";
    }
    
    // Example: Color based on score
    if (numericScore >= 0.7) scoreVariant = "success"; // Good match - primary color
    else if (numericScore >= 0.4) scoreVariant = "secondary"; // Okay match
    else if (numericScore > 0) scoreVariant = "outline"; // Low match
    else scoreVariant = "destructive"; // No match or very low
  }

  console.log(`JobCardHeader for "${title}": received jobUrl=${jobUrl}, hasUrl=${!!jobUrl}, score=${score}`);

  return (
    <div className="flex justify-between items-start gap-2">
      <div className="flex-grow space-y-1">
        {companyName && (
          <p className="text-md font-semibold">
            {renderLinkOrText(companyName)}
          </p>
        )}
        <CardTitle
          className="text-lg font-semibold leading-tight"
          onClick={(e) => { // Make title clickable to open details if no URL
             if (!jobUrl) {
                e.stopPropagation();
                onViewDetails();
             }
          }}
          style={{ cursor: jobUrl ? 'auto' : 'pointer' }}
        >
          {renderLinkOrText(title, true)}
        </CardTitle>
         {displayScore && scoreText !== "Score: N/A" && ( // Only display if score is valid
          <Badge variant={scoreVariant} className="mt-1 text-xs py-0.5 px-1.5 inline-flex items-center gap-1">
            <Star className="h-3 w-3" /> {scoreText}
          </Badge>
        )}
      </div>
      <div className="flex flex-col items-end space-y-1.5 shrink-0">
        {isUserLoggedIn && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSaveClick}
            disabled={isSavingThisJob}
            aria-label={isSaved ? "Unsave job" : "Save job"}
            className="h-8 w-8 text-muted-foreground hover:text-primary"
          >
            {isSavingThisJob ? (
              <Hourglass className="h-4 w-4 animate-spin" />
            ) : isSaved ? (
              <BookmarkCheck className="h-5 w-5 text-primary" />
            ) : (
              <Bookmark className="h-5 w-5" />
            )}
          </Button>
        )}
        {jobUrl && (
           <Button
            variant="default" 
            size="sm" 
            asChild
            className="py-1.5 px-3" 
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={jobUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center" 
            >
              View Source
              <ExternalLink className="ml-1.5 h-4 w-4" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};
